
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { RefreshCw, Pencil, Trash2, Zap, AlertTriangle } from 'lucide-react';
import { AgentCapability, agent_capability_type } from '@/types/agentic';
import { toast } from 'sonner';
import { deleteCapability } from '@/services/agentic/capabilityService';
import { hasCapabilityPermission, CapabilityPermissionLevel } from '@/services/agentic/permissionService';

interface CapabilityListProps {
  capabilities: AgentCapability[];
  onSelectCapability: (capability: AgentCapability) => void;
  onEditCapability: (capability: AgentCapability) => void;
  onRefresh: () => void;
}

const CapabilityList: React.FC<CapabilityListProps> = ({ 
  capabilities, 
  onSelectCapability,
  onEditCapability,
  onRefresh 
}) => {
  const [permissionCache, setPermissionCache] = useState<Record<string, Record<CapabilityPermissionLevel, boolean>>>({});
  const [loading, setLoading] = useState<boolean>(false);

  // Load permissions for all capabilities on mount
  useEffect(() => {
    const loadPermissions = async () => {
      setLoading(true);
      const permissionsMap: Record<string, Record<CapabilityPermissionLevel, boolean>> = {};
      
      for (const capability of capabilities) {
        permissionsMap[capability.id] = {
          view: await hasCapabilityPermission(capability.id, 'view'),
          execute: await hasCapabilityPermission(capability.id, 'execute'),
          edit: await hasCapabilityPermission(capability.id, 'edit'),
          create: await hasCapabilityPermission(capability.id, 'create')
        };
      }
      
      setPermissionCache(permissionsMap);
      setLoading(false);
    };
    
    loadPermissions();
  }, [capabilities]);

  // Function to get badge color based on capability type
  const getCapabilityTypeColor = (type: agent_capability_type) => {
    switch (type) {
      case 'course_management':
        return 'bg-blue-100 text-blue-800';
      case 'user_management':
        return 'bg-green-100 text-green-800';
      case 'content_generation':
        return 'bg-purple-100 text-purple-800';
      case 'data_analysis':
        return 'bg-amber-100 text-amber-800';
      case 'assessment':
        return 'bg-pink-100 text-pink-800';
      case 'notification':
        return 'bg-cyan-100 text-cyan-800';
      case 'scheduling':
        return 'bg-indigo-100 text-indigo-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleDelete = async (capability: AgentCapability) => {
    const canEdit = permissionCache[capability.id]?.edit;
    
    if (!canEdit) {
      toast.error("You don't have permission to delete this capability");
      return;
    }
    
    if (confirm(`Are you sure you want to delete the capability "${capability.name}"?`)) {
      try {
        await deleteCapability(capability.id);
        toast.success(`Capability "${capability.name}" deleted successfully`);
        onRefresh();
      } catch (error) {
        console.error('Error deleting capability:', error);
        toast.error('Failed to delete capability');
      }
    }
  };

  const canExecute = (capabilityId: string) => {
    return permissionCache[capabilityId]?.execute || false;
  };

  const canEdit = (capabilityId: string) => {
    return permissionCache[capabilityId]?.edit || false;
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={onRefresh}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>
      
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Description</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {capabilities.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-6 text-muted-foreground">
                  No capabilities found. Create your first capability to get started.
                </TableCell>
              </TableRow>
            ) : (
              capabilities.map((capability) => (
                <TableRow key={capability.id}>
                  <TableCell className="font-medium">{capability.name}</TableCell>
                  <TableCell>
                    <Badge className={getCapabilityTypeColor(capability.capability_type)}>
                      {capability.capability_type.replace('_', ' ')}
                    </Badge>
                  </TableCell>
                  <TableCell className="max-w-xs truncate">
                    {capability.description || 'No description'}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      {canExecute(capability.id) ? (
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => onSelectCapability(capability)}
                          title="Test capability"
                        >
                          <Zap className="h-4 w-4" />
                        </Button>
                      ) : (
                        <Button
                          variant="outline"
                          size="icon"
                          disabled
                          title="No permission to execute"
                        >
                          <AlertTriangle className="h-4 w-4 text-amber-500" />
                        </Button>
                      )}
                      
                      {canEdit(capability.id) ? (
                        <>
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => onEditCapability(capability)}
                            title="Edit capability"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => handleDelete(capability)}
                            title="Delete capability"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </>
                      ) : null}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default CapabilityList;
